import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { Refresh, Report } from '@mui/icons-material';
import ScriptIcon from '../components/ScriptIcon';
import { apiAppFetch } from '../utils/apiFetch';
import './../AccountPages.css';

const sanitizeQuotedValue = (value) => {
  if (value === null || value === undefined) return '';
  return String(value).replace(/^"+|"+$/g, '').trim();
};

const orderTypeText = (orderType, buySell) => {
  if (orderType === '1') return 'Market';
  if (orderType === '3') return 'Stop Loss';
  if (buySell === '1') return 'Buy Limit';
  if (buySell === '2') return 'Sell Limit';
  return 'Limit';
};

const RejectionLogs = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [rejectionLogs, setRejectionLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  const getRejectionLogs = async () => {
    try {
      setLoading(true);
      const response = await apiAppFetch('getTodayRejectionLog', {
        method: 'POST',
        body: JSON.stringify({}),
      });

      if (response.status === 401) {
        navigate('/login', { replace: true });
        return;
      }

      if (!response.ok) {
        setRejectionLogs([]);
        return;
      }

      const data = await response.json();
      setRejectionLogs(Array.isArray(data) ? data : []);
    } catch (error) {
      setRejectionLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getRejectionLogs();
  }, []);

  return (
    <div className={`account-page ${isDarkMode ? 'dark-theme' : 'light-theme'}`} data-theme={isDarkMode ? 'dark' : 'light'}>
      <div className="account-toolbar">
        <p>{rejectionLogs.length} rejected today</p>
        <button className="account-refresh" onClick={getRejectionLogs} disabled={loading} type="button">
          <Refresh />
          Refresh
        </button>
      </div>

      {loading ? (
        <div className="account-loading">
          <div className="account-spinner" />
          <p>Loading rejection logs...</p>
        </div>
      ) : rejectionLogs.length === 0 ? (
        <div className="account-empty">
          <Report />
          <h3>No rejections today</h3>
          <p>Rejected orders will appear here.</p>
        </div>
      ) : (
        <div className="account-panel">
          {rejectionLogs.map((log) => {
            const isBuy = String(log.buy_sell) === '1';
            const qty = Number(log.quantity || 0).toFixed(2);
            return (
              <div key={log.id} className="reject-row">
                <div className={`reject-rail ${isBuy ? 'buy' : ''}`} />
                <div className="script-with-icon script-with-icon-top">
                  <ScriptIcon name={log.stockName} size={28} />
                  <div className="script-identity-text">
                    <div className="reject-top">
                      <span className="reject-name">
                        {sanitizeQuotedValue(log.stockName)}
                        {String(log.lotVariant || '').toUpperCase() === 'MINI' && (
                          <span className="lot-variant-mini">MINI</span>
                        )}
                      </span>
                      <span className="reject-time">
                        {log.dateTime ? new Date(log.dateTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                      </span>
                    </div>
                    <div className="reject-reason">{sanitizeQuotedValue(log.remark) || 'Order rejected'}</div>
                    <div className="reject-pills">
                      <span className="reject-pill">{isBuy ? 'Buy' : 'Sell'}</span>
                      <span className="reject-pill">{orderTypeText(String(log.orderExecutionType), String(log.buy_sell))}</span>
                      <span className="reject-pill">QTY {isBuy ? qty : `-${qty}`}</span>
                      <span className="reject-pill">{String(log.orderTransactionType) === '1' ? 'Open' : 'Close'}</span>
                      {log.orderPrice ? <span className="reject-pill">@ {Number(log.orderPrice).toFixed(2)}</span> : null}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default RejectionLogs;
